// src/services/api.js
import axios from 'axios';

// 1) Définir l'URL de base en fonction de l'environnement
const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://automotive-database-api.onrender.com/api'
    : 'http://localhost:5001/api';

// 2) Créer l’instance axios avec cette base URL
const API = axios.create({
  baseURL: API_BASE_URL,
});

// Intercepteur pour ajouter le token JWT aux requêtes
API.interceptors.request.use((config) => {
  const token =
    localStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    (JSON.parse(localStorage.getItem('userData') || '{}')?.token);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log('🔑 [API] Token attached to request:', config.url, 'Token length:', token?.length);
  } else {
    console.warn('⚠️ [API] No token found for request:', config.url);
  }
  return config;
});

const resolveUserId = async () => {
  try {
    const { getUserId } = await import('../utils/auth');
    const id = getUserId();
    if (id) {
      return id;
    }
  } catch (error) {
    // Ignorer, on utilise un fallback
  }
  if (typeof localStorage !== 'undefined') {
    return localStorage.getItem('userId') || 'debug-user';
  }
  return 'debug-user';
};

// Services pour les documents
const documentService = {
  getAll: async (filters = {}) => {
    const response = await API.get('/documents', { params: filters });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await API.get(`/documents/${id}`);
    return response.data;
  },
  
  create: async (documentData) => {
    const response = await API.post('/documents', documentData);
    return response.data;
  },
  
  update: async (id, documentData) => {
    const response = await API.put(`/documents/${id}`, documentData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await API.delete(`/documents/${id}`);
    return response.data;
  },
  
  // Ajout de la méthode pour récupérer les statistiques des documents
  getDocumentStats: async () => {
    const response = await API.get('/documents/stats');
    return response.data;
  },
  
  // Méthode pour récupérer les filtres disponibles (catégories, statuts, etc.)
  getFilters: async () => {
    const response = await API.get('/documents/filters');
    return response.data;
  }
};

// Services pour l'authentification
const authService = {
  login: async (credentials) => {
    console.log('API service - login appelé avec:', credentials);
    const response = await API.post('/auth/login', credentials);
    console.log('API service - réponse du serveur:', response.data);

    const data = response.data; // ← attend { token, user } côté backend

    if (data?.token) {
      localStorage.setItem('authToken', data.token);     // ← indispensable pour l’intercepteur
    }
    if (data?.user) {
      localStorage.setItem('userData', JSON.stringify(data.user));
    }

    return data;
  },
  
  register: async (userData) => {
    const response = await API.post('/auth/register', userData);
    return response.data;
  },
  
  logout: async () => {
    try {
      await API.post('/auth/logout');
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      return { success: false, error: error.message };
    }
  },
  
  getCurrentUser: () => {
    const user = localStorage.getItem('userData');
    return user ? JSON.parse(user) : null;
  },
  
  // Méthode pour récupérer le profil de l'utilisateur actuel depuis l'API
  getProfile: async () => {
    const response = await API.get('/auth/me');
    return response.data;
  },
  
  // Méthode pour mettre à jour le profil de l'utilisateur
  updateProfile: async (userData) => {
    const response = await API.put('/auth/me', userData);
    return response.data;
  }
};

// Service pour les utilisateurs (pour les administrateurs)
const userService = {
  getAll: async () => {
    const response = await API.get('/users');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await API.get(`/users/${id}`);
    return response.data;
  },
  
  create: async (userData) => {
    const response = await API.post('/users', userData);
    return response.data;
  },
  
  update: async (id, userData) => {
    const response = await API.put(`/users/${id}`, userData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await API.delete(`/users/${id}`);
    return response.data;
  }
};

// Service pour les activités des utilisateurs
const userActivityService = {
  getAll: async () => {
    const response = await API.get('/user-activity');
    return response.data;
  },
  
  getByUser: async (userId) => {
    const response = await API.get(`/user-activity/${userId}`);
    return response.data;
  },
  
  // Récupérer les activités récentes pour le tableau de bord
  getRecent: async (limit = 10) => {
    const response = await API.get('/user-activity/recent', { params: { limit } });
    return response.data;
  }
};

// Service pour les articles lus (ReadArticles)
const readArticleService = {
  // Marquer un article comme lu
  markAsRead: async (articleId, legacyId) => {
    const userId = (await resolveUserId()) || localStorage.getItem('userId') || 'debug-user';
    return API.post('/read-articles/mark', { articleId, legacyId, userId });
  },
  markAsUnread: async (articleId, legacyId) => {
    const userId = (await resolveUserId()) || localStorage.getItem('userId') || 'debug-user';
    return API.post('/read-articles/unmark', { articleId, legacyId, userId });
  },
  // Récupérer la liste des articles lus pour l'utilisateur connecté
  getReadArticles: async () => {
    const userId = await resolveUserId();
    const params = typeof userId === 'string' && userId.trim().length > 0 ? { userId } : undefined;
    try {
      const response = await API.get('/read-articles/user', params ? { params } : undefined);
      return Array.isArray(response.data?.readArticles) ? response.data.readArticles : [];
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 400) {
        return [];
      }
      throw error;
    }
  }
};

const billingService = {
  getPlans: async () => {
    const response = await API.get('/billing/plans');
    return response.data;
  },

  getStatus: async () => {
    const response = await API.get('/billing/status');
    return response.data;
  },

  createCheckout: async (plan, billingCycle = 'monthly') => {
    const response = await API.post('/billing/create-checkout', { plan, billingCycle });
    return response.data;
  }
};

// Service pour les filtres sauvegardés de la base de données
const databaseFiltersService = {
  getAll: async () => {
    const userId = await resolveUserId();
    const response = await API.get(`/user-filters-database/user/${encodeURIComponent(userId)}`);
    return response.data;
  },
  save: async (name, filters) => {
    const userId = await resolveUserId();
    const filtersToSave = filters && typeof filters === 'object' ? filters : {};
    const response = await API.post('/user-filters-database/save', { userId, name, filters: filtersToSave });
    return response.data;
  },
  remove: async (id) => {
    const userId = await resolveUserId();
    const response = await API.delete(`/user-filters-database/${id}`, { params: { userId } });
    return response.data;
  },
  getById: async (id) => {
    const userId = await resolveUserId();
    const response = await API.get(`/user-filters-database/${id}`, { params: { userId } });
    return response.data;
  },
};

const userFiltersService = {
  // Récupérer tous les filtres sauvegardés
  getAll: async () => {
    const response = await API.get('/user-filters-conference');
    return response.data;
  },
  // Créer / mettre à jour un preset
  save: async (name, filters) => {
    // Récupérer l'userId depuis le localStorage ou utilitaire
    let userId;
    try {
      const { getUserId } = await import('../utils/auth');
      userId = getUserId();
    } catch (e) {
      userId = localStorage.getItem('userId') || 'debug-user';
    }
    // S'assurer que filters est bien un objet
    const filtersToSave = {
      Country: Array.isArray(filters.Country) ? filters.Country : [],
      Price: Array.isArray(filters.Price) ? filters.Price : [],
      presence: Array.isArray(filters.presence) ? filters.presence : [],
      type: Array.isArray(filters.type) ? filters.type : [],
      year: Array.isArray(filters.year) ? filters.year : [],
      query: typeof filters.query === 'string' ? filters.query : ''
    };
    const response = await API.post('/user-filters-conference/save', { userId, name, filters: filtersToSave });
    return response.data;
  },
  // Supprimer un preset
  remove: async (id) => {
    const response = await API.delete(`/user-filters-conference/${id}`);
    return response.data;
  }
};

// Service pour les favoris de conférences
const conferenceFavoritesService = {
  // Récupérer tous les favoris de l'utilisateur
  getAll: async () => {
    const userId = await resolveUserId();
    const response = await API.get('/conferences_fav', { params: { userId } });
    // Transformer le format de réponse pour correspondre à notre frontend
    const favorites = Array.isArray(response.data)
      ? response.data.map(fav => fav.conferenceId)
      : [];
    return {
      success: true,
      favorites: favorites
    };
  },

  // Ajouter une conférence aux favoris
  add: async (conferenceId) => {
    const userId = await resolveUserId();
    const response = await API.post('/conferences_fav', { userId, conferenceId });
    return {
      success: true,
      data: response.data
    };
  },

  // Supprimer une conférence des favoris
  remove: async (conferenceId) => {
    const userId = await resolveUserId();
    const response = await API.delete('/conferences_fav', {
      data: { userId, conferenceId }
    });
    return {
      success: true,
      data: response.data
    };
  }
};

// Service for patent saved filters
const patentFiltersService = {
  getAll: async () => {
    const userId = await resolveUserId();
    const response = await API.get(`/user-filters-patents/user/${encodeURIComponent(userId)}`);
    return response.data;
  },
  save: async (name, filters) => {
    const userId = await resolveUserId();
    const filtersToSave = filters && typeof filters === 'object' ? filters : {};
    const response = await API.post('/user-filters-patents/save', { userId, name, filters: filtersToSave });
    return response.data;
  },
  remove: async (id) => {
    const userId = await resolveUserId();
    const response = await API.delete(`/user-filters-patents/${id}`, { params: { userId } });
    return response.data;
  },
  getById: async (id) => {
    const userId = await resolveUserId();
    const response = await API.get(`/user-filters-patents/${id}`, { params: { userId } });
    return response.data;
  },
};

// Import conference filters service
export { conferenceFiltersService } from './conferenceFiltersService';

// --- exports : AJOUTE userFiltersService aux exports ---
export {
  API,
  documentService,
  authService,
  userService,
  userActivityService,
  readArticleService,
  billingService,
  userFiltersService,
  databaseFiltersService,
  conferenceFavoritesService,
  patentFiltersService
};

export default {
  documentService,
  authService,
  userService,
  userActivityService,
  readArticleService,
  billingService,
  userFiltersService,
  databaseFiltersService,
  conferenceFavoritesService,
  patentFiltersService
};
